import { httpClient } from "./API";
import type {
  AccidentReportPerson,
  AccidentReportPersonDetail,
  CreateAccidentReportPersonRequest,
  MlitFlowPerson,
  MlitFlowPersonRequest,
  RegistrationGuide,
  RegistrationLimit,
} from "@/models/accidentReportPersons";
import type { AxiosResponse } from "axios";

const BASE_URL = "core/internal/calgary/accident_report_persons";
const MLIT_BASE_URL = "core/internal/calgary/mlit_flow_persons";

/**
 * 事故報告責任者一覧取得。
 * 氏名は暗号化して保持しているためクライアント側では絞り込めない。部分一致はサーバ側で
 * 復号・正規化したうえで判定するため、検索条件として送信する。
 */
export const getAccidentReportPersonsAPI = async (
  officeId: number,
  name?: string,
  activeOnly = true,
): Promise<AxiosResponse<AccidentReportPerson[]>> => {
  return httpClient.get(`${BASE_URL}/`, {
    params: { officeId, name: name || undefined, activeOnly },
  });
};

/** 事故報告責任者詳細取得（研修プログラム単位の受講状況を含む） */
export const getAccidentReportPersonDetailAPI = async (
  personId: number,
): Promise<AxiosResponse<AccidentReportPersonDetail>> => {
  return httpClient.get(`${BASE_URL}/${personId}`);
};

/**
 * 登録上限の判定。
 * 登録画面を開いた直後と会社／営業所を変更した時点で呼び出す（登録実行時はサーバ側で再判定される）。
 */
export const getAccidentReportPersonRegistrationLimitAPI = async (
  officeId: number,
): Promise<AxiosResponse<RegistrationLimit>> => {
  return httpClient.get(`${BASE_URL}/registration_limit`, {
    params: { officeId },
  });
};

/**
 * 責任者登録数の案内表示の対象営業所を取得する。
 * 一覧の営業所単一選択とは無関係に、参照可能な営業所すべてを対象とする。
 */
export const getAccidentReportPersonRegistrationGuideAPI = async (): Promise<
  AxiosResponse<RegistrationGuide>
> => {
  return httpClient.get(`${BASE_URL}/registration_guide`);
};

/** 事故報告責任者登録 */
export const createAccidentReportPersonAPI = async (
  request: CreateAccidentReportPersonRequest,
): Promise<AxiosResponse<AccidentReportPerson>> => {
  return httpClient.post(`${BASE_URL}/`, request);
};

/** 事故報告責任者削除（物理削除は行わない） */
export const deleteAccidentReportPersonAPI = async (
  personId: number,
): Promise<AxiosResponse<void>> => {
  return httpClient.delete(`${BASE_URL}/${personId}`);
};

/** 国交省フロー組込み責任者一覧取得 */
export const getMlitFlowPersonsAPI = async (
  officeId: number,
): Promise<AxiosResponse<MlitFlowPerson[]>> => {
  return httpClient.get(`${MLIT_BASE_URL}/`, { params: { officeId } });
};

/** 国交省フロー組込み責任者登録 */
export const createMlitFlowPersonAPI = async (
  request: MlitFlowPersonRequest,
): Promise<AxiosResponse<MlitFlowPerson>> => {
  return httpClient.post(`${MLIT_BASE_URL}/`, request);
};

/** 国交省フロー組込み責任者削除（物理削除） */
export const deleteMlitFlowPersonAPI = async (
  id: number,
): Promise<AxiosResponse<void>> => {
  return httpClient.delete(`${MLIT_BASE_URL}/${id}`);
};
